import { useContext, useEffect, useRef, useState } from "react";
import PlusIcon from "../../assets/jobs/plus.svg?react";
import CloseIcon from "../../assets/close.svg?react";

import { useDispatch, useSelector } from "react-redux";
import { FeatureTableContext, JobContext, UserContext, VersionContext } from "../../Context";
import { setRightPanelActiveTab } from "../../store/appSlice";
import { StoreState } from "../../store/rootReducer";
import {
    canEditVersion,
    getVersionOwner,
    isDefaultVersion,
    isMyVersion,
    purgelocks,
} from "../../utils/versioningUtils";
import "./JobsPanel.css";
import { addJobsGraphicsLayerToMap, clearJobsGraphicsLayer, getJobByObjectId } from "../../utils/jobsUtils";
import { useDebouncedValue, useJobsList } from "./useJobsList";
import { toast } from "react-toastify";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import JobCard from "./JobCard/JobCard";
import JobDetails from "./JobDetails/JobDetails";
import NewJob from "./NewJob/NewJob";
import NewJobForObject from "./NewJob/NewJobForObject";
import { allJobsStatus, JobsPanelState, JobsStatus, jobStatusToString, JobsType } from "../../constants/Jobs";
import Loader from "../genericComponents/Loader/Loader";
import { RightPanelTabs } from "../../constants/Panels";
import NfsDropdown from "../genericComponents/NfsDropdown/NfsDropdown";
import InputField from "../genericComponents/InputField/InputField";
import { getMapView } from "../../utils/arcgisUtils";
import { SwitchToDefaultEventType, VersionIsDefaultEventType } from "../VersionControl/VersionControl";
import { getVersionInfoJson } from "../../utils/newVersioningUtils";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import type Graphic from "@arcgis/core/Graphic";
import type { VersionInfoExtended } from "@arcgis/core/versionManagement/support/jsonTypes";

const JobsPanel = () => {
    const dispatch = useDispatch();

    const [loading, setLoading] = useState<boolean>(false);
    const [canIEditVersion, setCanIEditVersion] = useState<boolean>(false);
    const [jobsPanelState, setJobsPanelState] = useState<JobsPanelState>(JobsPanelState.LIST);
    const [jobsSearchText, setJobsSearchText] = useState<string>("");
    const [versionHasLock, setVersionHasLock] = useState<boolean>(false);
    const [versionLockInfo, setVersionLockInfo] = useState<VersionInfoExtended | null>(null);
    const [unlockingVersion, setUnlockingVersion] = useState<boolean>(false);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const versionsSharedWithMe = useSelector((state: StoreState) => state.version.versionsSharedWithMe);
    const [selectedJobStatus, setSelectedJobStatus] = useState<JobsStatus>(JobsStatus.Alle);
    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);

    const [tabIndex, setTabIndex] = useState<number>(0);

    const userContext = useContext(UserContext);
    const jobsContext = useContext(JobContext);
    const selectedJob = jobsContext?.selectedJob.value;
    const versionContext = useContext(VersionContext);
    const versionServiceRef = versionContext?.versionServiceRef.value;

    const featureTableContext = useContext(FeatureTableContext);

    const highlightHandlesRef = useRef<ResourceHandle[]>([]);
    const hadSelectedJobRef = useRef<boolean>(false);

    const [refreshNonce, setRefreshNonce] = useState<number>(0);
    const listRef = useRef<HTMLDivElement | null>(null);

    const debouncedSearchText = useDebouncedValue(jobsSearchText, 300);
    const ownerUsername = userContext?.user.value?.username ?? null;
    const owner = tabIndex === 0 ? ownerUsername : null;
    // "Mine jobber" without a resolved user has no owner to filter by; querying with owner: null
    // would silently return everyone's jobs, so skip the query and show an empty list instead.
    const skipJobsQuery = tabIndex === 0 && !ownerUsername;

    const {
        jobs,
        total,
        page,
        totalPages,
        loading: jobsLoading,
        setPage,
    } = useJobsList({
        status: selectedJobStatus,
        searchText: debouncedSearchText,
        owner,
        versionGuid: activeVersion?.versionIdentifier.guid,
        refreshNonce,
        skip: skipJobsQuery,
    });

    const checkIfDefaultVersion = () => {
        if (activeVersion && versionServiceRef?.current) {
            return isDefaultVersion(activeVersion, versionServiceRef?.current);
        }
        return false;
    };

    const checkIfVersionHasLock = async () => {
        let versionGuid = activeVersion?.versionIdentifier.guid;
        versionGuid = versionGuid?.replace("{", "").replace("}", "");
        if (!versionGuid) {
            setVersionHasLock(false);
            setVersionLockInfo(null);
            return false;
        }
        const freshVersionInfo = await getVersionInfoJson(versionGuid);
        const hasLock = freshVersionInfo?.isLocked ?? false;
        setVersionHasLock(hasLock);
        setVersionLockInfo(freshVersionInfo);
        return hasLock;
    };

    const isDefaultActiveVersion = () => {
        return checkIfDefaultVersion();
    };

    const isLockOwner = () => {
        const lockOwner = (versionLockInfo?.lockOwner ?? "").toLowerCase();
        const user = userContext?.user.value;
        if (!user || !lockOwner) {
            return false;
        }

        const username = user.username.toLowerCase();
        const userEmail = user.email?.toLowerCase() ?? "";
        const lockOwnerNameOnly = lockOwner.split("@")[0];
        const usernameOnly = username.split("@")[0];

        return (
            lockOwner === username ||
            lockOwner === userEmail ||
            lockOwnerNameOnly === usernameOnly ||
            lockOwnerNameOnly === userEmail.split("@")[0]
        );
    };

    const canUnlockVersion = () => {
        if (!activeVersion || isDefaultActiveVersion()) {
            return false;
        }
        const user = userContext?.user.value;
        if (!user) {
            return false;
        }

        return isLockOwner() || isMyVersion(activeVersion, user);
    };

    const unlockVersion = async () => {
        if (!activeVersion) {
            return;
        }
        setUnlockingVersion(true);
        try {
            await purgelocks(activeVersion.versionIdentifier.name);
            await checkIfVersionHasLock();
        } finally {
            setUnlockingVersion(false);
        }
    };

    const renderVersionLockAction = () => {
        if (isDefaultActiveVersion()) {
            return (
                <NfsButton className="no-flex" onClick={checkIfVersionHasLock}>
                    Sjekk lås på nytt
                </NfsButton>
            );
        }

        if (canUnlockVersion()) {
            return (
                <>
                    <NfsButton className="no-flex" onClick={unlockVersion}>
                        Lås opp versjon
                    </NfsButton>
                    <NfsButton className="no-flex" outlined onClick={checkIfVersionHasLock}>
                        Sjekk lås på nytt
                    </NfsButton>
                </>
            );
        }

        return (
            <NfsButton className="no-flex" onClick={checkIfVersionHasLock}>
                Sjekk lås på nytt
            </NfsButton>
        );
    };

    const openJob = async (job: Graphic) => {
        setLoading(true);
        let freshJob: Graphic | null;
        try {
            freshJob = await getJobByObjectId(job.attributes.objectid);
        } finally {
            setLoading(false);
        }

        if (!freshJob) {
            toast.warn("Jobben finnes ikke lenger. Listen oppdateres.");
            refreshJobs();
            return;
        }

        let shouldSwitch = false;
        if (
            freshJob.attributes.status === JobsStatus.QA1 ||
            freshJob.attributes.status === JobsStatus.QA2 ||
            freshJob.attributes.jobb_type === JobsType.DRONE_CONTROL
        ) {
            if (!checkIfDefaultVersion()) {
                if (globalThis.confirm("Du står ikke i Planroot. Skal vi bytte til Planroot før vi åpner jobben?")) {
                    globalThis.dispatchEvent(new CustomEvent(SwitchToDefaultEventType));
                    shouldSwitch = true;
                }
            }
        }

        if (shouldSwitch) {
            const switchAndOpenJob = () => {
                setJobsPanelState(JobsPanelState.JOB);
                jobsContext?.selectedJob.set(freshJob);
                globalThis.removeEventListener(VersionIsDefaultEventType, switchAndOpenJob);
            };
            globalThis.addEventListener(VersionIsDefaultEventType, switchAndOpenJob);
        } else {
            setJobsPanelState(JobsPanelState.JOB);
            jobsContext?.selectedJob.set(freshJob);
        }
    };

    const openNewJob = async () => {
        const hasLock = await checkIfVersionHasLock();
        if (hasLock) {
            return;
        }

        setJobsPanelState(JobsPanelState.NEW);
    };

    const renderJobs = () => {
        if (jobs.length === 0) {
            return <div className="empty-list">Ingen jobber funnet</div>;
        }
        return jobs.map((job) => (
            <JobCard key={job.attributes.objectid} jobObject={job} onClick={() => openJob(job)} />
        ));
    };

    const renderJobsSection = () => (
        <>
            <div className="jobs-list" ref={listRef}>
                {renderJobs()}
            </div>
            {total > 0 && (
                <div className="jobs-pagination-controls">
                    <div className="jobs-pagination-info">
                        <span>{total === 1 ? "1 treff" : `${total} treff`}</span>
                    </div>
                    <div className="jobs-pagination-buttons">
                        <NfsButton
                            onClick={() => setPage(Math.max(1, page - 1))}
                            disabled={page <= 1}
                            small
                            outlined
                            className="jobs-pagination-button"
                        >
                            Forrige
                        </NfsButton>
                        <span>
                            Side {page} av {totalPages}
                        </span>
                        <NfsButton
                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                            disabled={page >= totalPages}
                            small
                            outlined
                            className="jobs-pagination-button"
                        >
                            Neste
                        </NfsButton>
                    </div>
                </div>
            )}
        </>
    );

    /**
     * Forces the jobs list to refetch without changing the query. Signature is unchanged so
     * NewJob, NewJobForObject and JobDetails keep working untouched.
     */
    const refreshJobs = () => {
        setRefreshNonce((nonce) => nonce + 1);
    };

    const closePanel = () => {
        jobsContext?.selectedJob.set(null);
        setJobsPanelState(JobsPanelState.LIST);
        dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
    };

    const clearHighlightedFeatures = () => {
        highlightHandlesRef.current.forEach((handle) => {
            handle.remove();
        });
        highlightHandlesRef.current = [];
    };

    const storeHighlightHandle = (handle: ResourceHandle | null) => {
        clearHighlightedFeatures();
        if (handle) {
            highlightHandlesRef.current.push(handle);
        }
    };

    useEffect(() => {
        clearHighlightedFeatures();
        return () => {
            clearHighlightedFeatures();
        };
    }, []);

    useEffect(() => {
        if (activeVersion) {
            const user = userContext?.user.value;
            canEditVersion(activeVersion, versionsSharedWithMe ?? [], user).then((result) => {
                setCanIEditVersion(result);
            });
        }
    }, [activeVersion, versionsSharedWithMe]);

    useEffect(() => {
        if (selectedJob) {
            hadSelectedJobRef.current = true;
            dispatch(setRightPanelActiveTab(RightPanelTabs.JOBS));
            checkIfVersionHasLock();
        } else if (hadSelectedJobRef.current) {
            // Catches refresh for paths (AlertsList, FeatureEditor) where opening a job doesn't change jobsPanelState.
            hadSelectedJobRef.current = false;
            refreshJobs();
        }
    }, [selectedJob]);

    useEffect(() => {
        clearHighlightedFeatures();
        if (jobsPanelState === JobsPanelState.LIST) {
            refreshJobs();
            checkIfVersionHasLock();
        }
    }, [jobsPanelState]);

    useEffect(() => {
        if (jobsContext?.preLoadJobConfig.value !== null && jobsPanelState !== JobsPanelState.NEW_FOR_OBJECT) {
            setJobsPanelState(JobsPanelState.NEW_FOR_OBJECT);
        }
    }, [jobsContext?.preLoadJobConfig.value]);

    useEffect(() => {
        if (rightPanelActiveTab === RightPanelTabs.JOBS) {
            addJobsGraphicsLayerToMap();
            checkIfVersionHasLock();
        } else {
            setJobsPanelState(JobsPanelState.LIST);
            jobsContext?.selectedJob.set(null);
            clearJobsGraphicsLayer();
            clearHighlightedFeatures();
        }
    }, [rightPanelActiveTab]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: 0 });
    }, [page]);

    useEffect(() => {
        const mapview = getMapView();
        if (!mapview) return;
        const lightLayer = mapview.map?.findLayerById("Lys");
        if (lightLayer && !featureTableContext?.activeLayer) {
            featureTableContext?.activeLayer.set(lightLayer as FeatureLayer);
        }
    }, []);

    return (
        <div className={`jobs-panel`}>
            {(loading || jobsLoading || unlockingVersion) && <Loader overlay="element" targetElement=".jobs-panel" />}
            {versionHasLock && (
                <div style={{ height: "unset" }} className="jobs-panel-content jobs-panel-locked-content">
                    <div className="flex-row space-between">
                        <span>Jobber i NFS 2</span>
                        <button type="button" className="empty-button" onClick={closePanel}>
                            <CloseIcon />
                        </button>
                    </div>
                    <div className="jobs-panel-lock-message">
                        <span className="bold">Aktiv versjon er låst</span>
                        <span>
                            Jobbpanelet er midlertidig sperret for å unngå feil ved redigering mens versjonen er låst.
                        </span>
                        {!isDefaultActiveVersion() && versionLockInfo?.lockOwner && (
                            <span>Låst av: {versionLockInfo.lockOwner}</span>
                        )}
                        {!isDefaultActiveVersion() && <span>Versjonseier: {getVersionOwner(activeVersion)}</span>}
                    </div>
                    <div className="flex-row space-between w-100">{renderVersionLockAction()}</div>
                </div>
            )}
            {!versionHasLock && jobsPanelState === "list" && !selectedJob && (
                <div className="jobs-panel-content">
                    <div className="flex-row space-between">
                        <span>Jobber i NFS 2</span>
                        <NfsButton onClick={refreshJobs} className="no-flex">
                            Oppdater
                        </NfsButton>
                        <button type="button" className="empty-button" onClick={closePanel}>
                            <CloseIcon />
                        </button>
                    </div>
                    {canIEditVersion && (
                        <div className="flex-row space-between">
                            <NfsButton onClick={openNewJob}>
                                <div className="flex-row center small-gap">
                                    <PlusIcon />
                                    <span>Opprett ny jobb</span>
                                </div>
                            </NfsButton>
                        </div>
                    )}
                    <div className="flex-col" style={{ overflow: "hidden" }}>
                        <Tabs
                            className="h-100 flex-col small-gap"
                            selectedTabClassName="selected"
                            selectedIndex={tabIndex}
                            onSelect={(index) => {
                                setTabIndex(index);
                                setPage(1);
                            }}
                        >
                            <TabList className="jobs-tab-list">
                                <Tab className="jobs-tab-button">Mine jobber</Tab>
                                <Tab className="jobs-tab-button">Alle</Tab>
                            </TabList>
                            <NfsDropdown
                                options={allJobsStatus()}
                                selectedOption={selectedJobStatus as unknown as string}
                                displayValueFunction={(status) =>
                                    jobStatusToString(parseInt(status as string) as JobsStatus)
                                }
                                setSelectedOption={(setOption) => {
                                    setSelectedJobStatus(parseInt(setOption!) as JobsStatus);
                                    setPage(1);
                                }}
                                placeholder="Velg status"
                            />
                            <InputField
                                label=""
                                w100
                                type="text"
                                onChange={(value) => {
                                    setJobsSearchText(value);
                                    setPage(1);
                                }}
                                value={jobsSearchText}
                                inputFieldId="job-search"
                                placeholder="Søk"
                            />
                            <TabPanel className={`jobs-tab-panel ${tabIndex !== 0 ? "hidden" : ""}`} hidden={tabIndex !== 0}>
                                {tabIndex === 0 && renderJobsSection()}
                            </TabPanel>
                            <TabPanel className={`jobs-tab-panel ${tabIndex !== 1 ? "hidden" : ""}`} hidden={tabIndex !== 1}>
                                {tabIndex === 1 && renderJobsSection()}
                            </TabPanel>
                        </Tabs>
                    </div>
                </div>
            )}
            {!versionHasLock && selectedJob && (
                <JobDetails
                    jobObject={selectedJob}
                    setJobPanelState={setJobsPanelState}
                    setLoading={setLoading}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            )}
            {!versionHasLock && jobsPanelState === JobsPanelState.NEW && !selectedJob && (
                <NewJob
                    setJobPanelState={setJobsPanelState}
                    refreshJobs={refreshJobs}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            )}
            {!versionHasLock && jobsPanelState === JobsPanelState.NEW_FOR_OBJECT && !selectedJob && (
                <NewJobForObject
                    setJobPanelState={setJobsPanelState}
                    refreshJobs={refreshJobs}
                    storeHighlightHandle={storeHighlightHandle}
                    clearHighlightedFeatures={clearHighlightedFeatures}
                />
            )}
        </div>
    );
};

export default JobsPanel;
